// Mass Balance hub — a shared DATASET selector above two tabs: the Refinery Mass Balance utility
// (AORA production/movement/inventory) and the OAS Diagnostic dashboard. A dataset = one
// customer/site's files (both formats, all months). The active dataset is BOTH the upload target
// and the view for both tabs; switching it re-renders both tabs from that customer's data.
// See MASS_BALANCE_ENHANCEMENT_PLAN.md Phase 3.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Tabs, Tab, Select, MenuItem, Typography, Stack, TextField, Button, Tooltip, CircularProgress, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, IconButton, Divider, Alert } from '@mui/material';
import { CloudUpload, CreateNewFolder, FolderOpen, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import * as XLSX from 'xlsx';
import MassBalanceUtility from './MassBalanceUtility';
import MassBalanceReconciliation from './MassBalanceReconciliation';
import { listDatasets, DatasetInfo, MBTabHandle, listPeriods, listUploads, deleteUpload, StoredFile, MBAuthError } from '../utils/massBalanceStore';
import { classify } from '../utils/massBalanceOas';

interface MgrFile extends StoredFile { utility: 'oas' | 'refinery'; }

const LS_ACTIVE = 'mb-active-dataset';

const MassBalanceHub: React.FC = () => {
  const { getIdTokenClaims } = useAuth0();
  const [tab, setTab] = useState<'refinery' | 'oas'>('refinery');
  const [datasets, setDatasets] = useState<DatasetInfo[]>([]);
  const [active, setActive] = useState<string>(() => { try { return localStorage.getItem(LS_ACTIVE) || ''; } catch { return ''; } });
  const [creating, setCreating] = useState(false);
  // A dataset typed in THIS session that has no files on the server yet. Previously the selector
  // injected whatever `active` held, which came from localStorage -- so a dataset that was renamed or
  // deleted server-side came back as a phantom "<name> · new" entry that nothing could clear.
  const [pendingNew, setPendingNew] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [reloadKey, setReloadKey] = useState(0); // bump to make both tabs re-load the active dataset (after delete)
  const [mgrOpen, setMgrOpen] = useState(false);
  const [mgrFiles, setMgrFiles] = useState<MgrFile[]>([]);
  const [mgrLoading, setMgrLoading] = useState(false);
  // An expired session used to render as "No files saved in this dataset yet." -- the catch below
  // swallowed the 401 and the empty list looked like an empty DATASET. Reported 9 Oct: a user with
  // a 2-hour-old session opened Manage files, saw nothing, and concluded her uploads were missing.
  const [mgrError, setMgrError] = useState<string>('');
  const oasRef = useRef<MBTabHandle>(null);
  const refineryRef = useRef<MBTabHandle>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  const idToken = useCallback(async () => (await getIdTokenClaims())?.__raw, [getIdTokenClaims]);

  const refreshDatasets = useCallback(async () => {
    try {
      const ds = await listDatasets(await idToken());
      setDatasets(ds);
      setActive((cur) => cur || ds[0]?.name || '');
    } catch { /* auth expiry is surfaced inside each tab */ }
  }, [idToken]);

  useEffect(() => { refreshDatasets(); }, [refreshDatasets]);
  useEffect(() => { if (active) { try { localStorage.setItem(LS_ACTIVE, active); } catch { /* ignore */ } } }, [active]);

  // Existing datasets plus one created in this session that has no files on the server yet.
  const options = React.useMemo(() => {
    const names = new Set(datasets.map((d) => d.name));
    if (pendingNew && !names.has(pendingNew)) names.add(pendingNew);
    return Array.from(names).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [datasets, pendingNew]);

  // The remembered dataset can stop existing -- renamed or deleted since this browser last looked.
  // Fall back to the first real one instead of leaving the user pointed at a name the server does not
  // have, which reads as "my data is gone" when it is simply under a different name now.
  useEffect(() => {
    if (!datasets.length) return;
    if (pendingNew && active === pendingNew) return;          // a genuinely new, still-empty dataset
    if (datasets.some((d) => d.name === active)) { if (pendingNew) setPendingNew(''); return; }
    setActive(datasets[0].name);
  }, [datasets, active, pendingNew]);

  const createDataset = () => {
    const n = newName.trim();
    if (!n) return;
    setPendingNew(n); setActive(n); setCreating(false); setNewName('');
  };

  // THE single upload path for the whole feature: sort each file into its format family (OAS vs AORA)
  // and hand it to that tab's own parser via the imperative handle. No per-tab upload surfaces needed.
  const onUpload = async (files: File[]) => {
    if (!active) { setMsg('Pick or create a dataset first.'); return; }
    const xls = files.filter((f) => /\.(xlsx|xlsm|xls)$/i.test(f.name));
    if (!xls.length) { setMsg('No spreadsheet (.xlsx/.xls) files found.'); return; }
    setBusy(true);
    const oasFiles: File[] = []; const aoraFiles: File[] = [];
    for (const f of xls) {
      let isOas = false;
      try { isOas = !!classify(XLSX.read(await f.arrayBuffer(), { type: 'array', cellDates: true })).kind; } catch { /* unreadable → treat as AORA */ }
      (isOas ? oasFiles : aoraFiles).push(f);
    }
    try {
      if (oasFiles.length) await oasRef.current?.ingest(oasFiles);
      if (aoraFiles.length) await refineryRef.current?.ingest(aoraFiles);
      await refreshDatasets();
      // Make both tabs re-load the dataset, exactly as a delete already does. Without this an upload
      // refreshed only the hub's dataset counts, so a newly added month did not appear until the user
      // reloaded the page. Safe to fire straight away even though the snapshot is computed
      // asynchronously: the month view falls back to parsing the stored workbook whenever a period has
      // no snapshot yet, so the numbers are right either way and the snapshot takes over once written.
      setReloadKey((k) => k + 1);
      setMsg(`Added to "${active}" — ${oasFiles.length} diagnostic · ${aoraFiles.length} refinery file(s).`);
    } finally { setBusy(false); }
  };

  // Unified saved-files manager: every file in the active dataset, across both tabs, in one place.
  const loadMgr = useCallback(async () => {
    if (!active) { setMgrFiles([]); return; }
    setMgrLoading(true); setMgrError('');
    try {
      const t = await idToken();
      const all: MgrFile[] = [];
      for (const utility of ['oas', 'refinery'] as const) {
        const ps = await listPeriods(utility, active, t);
        for (const p of ps) {
          const files = await listUploads(utility, active, p, t);
          files.forEach((f) => all.push({ ...f, utility }));
        }
      }
      all.sort((a, b) => (b.period || '').localeCompare(a.period || '') || a.utility.localeCompare(b.utility) || (a.kind || '').localeCompare(b.kind || ''));
      setMgrFiles(all);
    } catch (e) {
      // Say WHICH failure this is. Silence here is indistinguishable from "this dataset is empty".
      setMgrFiles([]);
      setMgrError(e instanceof MBAuthError
        ? 'Your session has expired, so the file list could not be loaded. Refresh the page or sign in again — your files are still saved.'
        : 'The file list could not be loaded. Refresh the page and try again.');
    } finally { setMgrLoading(false); }
  }, [active, idToken]);
  const openFileManager = () => { setMgrOpen(true); loadMgr(); };
  const removeFile = async (f: MgrFile) => {
    const t = await idToken();
    if (await deleteUpload(f.utility, active, f.period, f.uploadId, t)) {
      setMgrFiles((prev) => prev.filter((x) => !(x.uploadId === f.uploadId && x.utility === f.utility)));
      await refreshDatasets();
      setReloadKey((k) => k + 1); // make both tabs re-load the dataset so the removed file leaves the view
    }
  };

  return (
    <Box>
      {/* Active dataset — the customer/site BOTH tabs render, and the target new uploads go into. */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">Dataset:</Typography>
        {!creating ? (
          <>
            <Select size="small" value={options.includes(active) ? active : ''} onChange={(e) => setActive(e.target.value)} sx={{ minWidth: 220 }} displayEmpty>
              {options.length === 0 && <MenuItem value=""><em>No datasets yet — upload to create one</em></MenuItem>}
              {options.map((name) => {
                const d = datasets.find((x) => x.name === name);
                const cnt = d ? d.oas + d.refinery : 0;
                return <MenuItem key={name} value={name}>{name}{cnt ? ` · ${cnt} file${cnt > 1 ? 's' : ''}` : ' · new'}</MenuItem>;
              })}
            </Select>
            <Button size="small" variant="outlined" onClick={() => setCreating(true)}>+ New dataset</Button>
          </>
        ) : (
          <>
            <TextField size="small" autoFocus placeholder="Dataset name" value={newName}
              onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') createDataset(); }} sx={{ minWidth: 220 }} />
            <Button size="small" variant="contained" onClick={createDataset} disabled={!newName.trim()}>Create</Button>
            <Button size="small" onClick={() => { setCreating(false); setNewName(''); }}>Cancel</Button>
          </>
        )}
        <Tooltip title="Both tabs show the selected dataset, and new uploads go into it. Switch datasets to view a different customer's data in both tabs.">
          <Typography variant="caption" color="text.secondary">shown in both tabs · upload target</Typography>
        </Tooltip>
      </Stack>

      {/* THE one upload path — files or a whole folder, auto-sorted into the right tab, saved to the active dataset. */}
      <Box
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const fs = Array.from(e.dataTransfer.files || []); if (fs.length) onUpload(fs); }}
        sx={{ p: 2, mb: 2, borderRadius: 1, borderStyle: 'dashed', borderWidth: 2, borderColor: 'divider', bgcolor: 'action.hover' }}>
        <input ref={fileInput} type="file" accept=".xlsx,.xlsm,.xls" hidden multiple onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length) onUpload(fs); if (fileInput.current) fileInput.current.value = ''; }} />
        <input ref={folderInput} type="file" hidden multiple {...({ webkitdirectory: '' } as any)} onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length) onUpload(fs); if (folderInput.current) folderInput.current.value = ''; }} />
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2">Upload data to <strong>{active || '—'}</strong>:</Typography>
          <Button size="small" variant="contained" startIcon={<CloudUpload />} disabled={busy || !active} onClick={() => fileInput.current?.click()}>Files</Button>
          <Button size="small" variant="outlined" startIcon={<CreateNewFolder />} disabled={busy || !active} onClick={() => folderInput.current?.click()}>Folder</Button>
          <Button size="small" variant="text" startIcon={<FolderOpen />} disabled={!active} onClick={openFileManager}>Manage files</Button>
          {busy && <CircularProgress size={18} />}
          <Typography variant="caption" color="text.secondary">
            Drop files/folders here or use the buttons — each file is auto-sorted into the Refinery or Diagnostic tab. One upload for both.
          </Typography>
        </Stack>
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="refinery" label="Refinery Mass Balance" />
        <Tab value="oas" label="Diagnostic" />
      </Tabs>
      {/* Keep BOTH views mounted and toggle visibility so switching tabs never wipes uploaded data. */}
      <Box sx={{ display: tab === 'refinery' ? 'block' : 'none' }}><MassBalanceUtility ref={refineryRef} dataset={active} reloadKey={reloadKey} onUploaded={refreshDatasets} isActive={tab === 'refinery'} /></Box>
      <Box sx={{ display: tab === 'oas' ? 'block' : 'none' }}><MassBalanceReconciliation ref={oasRef} dataset={active} reloadKey={reloadKey} onUploaded={refreshDatasets} isActive={tab === 'oas'} /></Box>

      {/* Unified saved-files manager — every file in the active dataset, both tabs, one place. */}
      <Dialog open={mgrOpen} onClose={() => setMgrOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Saved files — {active || '—'}</DialogTitle>
        <DialogContent dividers>
          {mgrLoading ? (
            <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>
          ) : mgrError ? (
            <Alert severity="warning" action={<Button size="small" onClick={() => window.location.reload()}>Reload</Button>}>{mgrError}</Alert>
          ) : mgrFiles.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No files saved in this dataset yet.</Typography>
          ) : (
            <Stack spacing={0.5}>
              {mgrFiles.map((f, i) => (
                <React.Fragment key={`${f.utility}-${f.uploadId}`}>
                  {(i === 0 || mgrFiles[i - 1].period !== f.period) && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mt: i ? 1 : 0 }}>{f.period}</Typography>
                  )}
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip size="small" variant="outlined" label={f.utility === 'oas' ? 'Diagnostic' : 'Refinery'} color={f.utility === 'oas' ? 'info' : 'default'} />
                    <Chip size="small" variant="outlined" label={f.kind} sx={{ textTransform: 'capitalize' }} />
                    <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.filename}>{f.filename}</Typography>
                    <Tooltip title="Delete this file"><IconButton size="small" onClick={() => removeFile(f)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Stack>
                  <Divider light />
                </React.Fragment>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setMgrOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!msg} autoHideDuration={5000} onClose={() => setMsg('')} message={msg} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default MassBalanceHub;
